/**
 * JerryDrivePublic — publiczny widok kierowcy (bez logowania) dostępny pod /jerry-drive
 *
 * Krok 1: wybór kierowcy z dropdown (lista od managera)
 * Krok 2: wybór akcji „Rozpocznij dzień autem" / „Zakończ dzień autem"
 * Krok 3: formularz + obowiązkowe 6 zdjęć (przód, tył, lewy bok, prawy bok, wnętrze, licznik)
 *
 * Bez auth — backend ogranicza moduł do nowysacz.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { compressImageFast } from '../../utils/compressImage';

const API = process.env.REACT_APP_BACKEND_URL;
const LOCATION = 'nowysacz';

const PHOTO_FIELDS = [
  { key: 'front', label: '📸 Przód auta' },
  { key: 'back', label: '📸 Tył auta' },
  { key: 'left', label: '📸 Lewy bok' },
  { key: 'right', label: '📸 Prawy bok' },
  { key: 'interior', label: '📸 Wnętrze' },
  { key: 'dashboard', label: '📸 Licznik km' },
];

const colors = {
  bg: '#0a0f1e',
  panel: '#111827',
  border: '#1f2937',
  text: '#f1f5f9',
  muted: '#94a3b8',
  blue: '#3b82f6',
  green: '#10b981',
  red: '#ef4444',
  gold: '#f59e0b',
  // Nowa akcentowa paleta - elektryczny niebieski / cyan zamiast pomarańczowego
  accent: '#06b6d4',
  accentSoft: '#0e7490',
  teal: '#14b8a6',
  indigo: '#6366f1',
};

const labelStyle = { display: 'block', fontSize: 13, color: colors.muted, marginBottom: 6, fontWeight: 600 };
const inputStyle = {
  width: '100%', padding: '12px', borderRadius: 10,
  border: `1px solid ${colors.border}`, background: '#0b1220', color: colors.text, fontSize: 15, boxSizing: 'border-box',
};

// === PHOTO PICKER ===
// OPTIMISTIC UI: zdjęcie pojawia się NATYCHMIAST po kompresji (Blob URL),
// a upload do backendu leci W TLE. Kierowca nie czeka — może od razu robić
// następne zdjęcie. Gdy upload się skończy, podmieniamy preview na URL serwera.
// W razie błędu uploadu — pokazujemy strzałkę „Spróbuj ponownie".
const PhotoPicker = ({ label, value, onChange, testId, slot, onUploadState }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);            // tylko podczas kompresji (UI lock)
  const [uploading, setUploading] = useState(false);  // upload w tle
  const [uploadFailed, setUploadFailed] = useState(false);
  const [localPreview, setLocalPreview] = useState(null);  // Blob URL — zanim backend odpowie
  const pendingBlobRef = useRef(null);                // Blob trzymany w razie retry

  // Powiadom rodzica o zmianie stanu uploadu (potrzebne żeby zablokować "Wyślij" gdy coś się jeszcze wysyła / padło)
  useEffect(() => {
    onUploadState?.(slot, { uploading, failed: uploadFailed });
  }, [uploading, uploadFailed, slot, onUploadState]);

  // Cleanup Blob URL gdy komponent znika / value zmienia się na server URL
  useEffect(() => {
    return () => {
      if (localPreview) {
        try { URL.revokeObjectURL(localPreview); } catch (_) { /* ignore */ }
      }
    };
  }, [localPreview]);

  const doUpload = async (blob) => {
    setUploading(true);
    setUploadFailed(false);
    try {
      const fd = new FormData();
      fd.append('slot', slot || 'photo');
      fd.append('file', blob, `${slot || 'photo'}.jpg`);
      const r = await axios.post(`${API}/api/jerry-drive/photo-upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const url = r?.data?.url;
      if (!url) throw new Error('Brak URL z serwera');
      onChange(url);
      pendingBlobRef.current = null;
      // localPreview może zostać do momentu gdy ten komponent się re-renderuje z value=url
      // (efekt useEffect powyżej zwolni Blob URL przy unmount)
    } catch (err) {
      setUploadFailed(true);
      toast.error('Błąd wysyłki: ' + (err?.response?.data?.detail || err.message) + ' — kliknij retry');
    } finally {
      setUploading(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      // 1) Szybka kompresja → Blob + instant preview
      const { blob, previewUrl } = await compressImageFast(file, { maxWidth: 1024, quality: 0.6 });
      // 2) Pokaż natychmiast — kierowca widzi że "się stało"
      setLocalPreview(previewUrl);
      pendingBlobRef.current = blob;
      setBusy(false);  // <-- KLUCZOWE: odblokuj UI od razu
      // 3) Upload w tle (nie czekamy w handleFile)
      doUpload(blob);
    } catch (err) {
      setBusy(false);
      toast.error('Błąd kompresji: ' + err.message);
    }
  };

  const retry = () => {
    if (pendingBlobRef.current) doUpload(pendingBlobRef.current);
  };

  // Co pokazujemy w polu zdjęcia?
  //   • value (URL z serwera) → docelowo
  //   • localPreview (Blob URL) → po kompresji, przed uploadem
  //   • nic → przycisk "Zrób zdjęcie"
  const previewSrc = value
    ? (value.startsWith('http') || value.startsWith('data:') ? value : `${API}${value}`)
    : localPreview;

  return (
    <div style={{
      background: colors.panel, border: `1px solid ${previewSrc ? colors.green : colors.border}`,
      borderRadius: 10, padding: 10, display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ fontSize: 12, color: previewSrc ? colors.green : colors.muted, fontWeight: 700 }}>
        {previewSrc ? '✅ ' : ''}{label}
      </div>
      {previewSrc ? (
        <div style={{ position: 'relative' }}>
          <img src={previewSrc} alt={label} style={{ width: '100%', borderRadius: 8, display: 'block', maxHeight: 200, objectFit: 'cover' }} />
          {/* Wskaźnik "wysyłam w tle" */}
          {uploading && (
            <div style={{
              position: 'absolute', bottom: 6, left: 6, padding: '4px 10px',
              background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 11, borderRadius: 999,
              fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#fbbf24', animation: 'pulse 1.2s ease-in-out infinite' }} />
              Wysyłam…
            </div>
          )}
          {uploadFailed && !uploading && (
            <button type="button" onClick={retry} style={{
              position: 'absolute', bottom: 6, left: 6, padding: '4px 10px',
              background: '#ef4444', color: '#fff', fontSize: 11, borderRadius: 999,
              fontWeight: 700, border: 'none', cursor: 'pointer',
            }}>↻ Spróbuj ponownie</button>
          )}
          <button
            type="button"
            onClick={() => { onChange(null); setLocalPreview(null); pendingBlobRef.current = null; }}
            style={{
              position: 'absolute', top: 4, right: 4,
              background: 'rgba(0,0,0,0.7)', color: 'white', border: 'none',
              borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontSize: 14,
            }}>
            ✕
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={testId}
          style={{
            padding: '10px', borderRadius: 8,
            background: busy ? colors.muted : colors.blue, color: 'white',
            border: 'none', cursor: busy ? 'wait' : 'pointer', fontWeight: 700, fontSize: 13,
          }}>
          {busy ? '⏳ Przetwarzam…' : '📷 Zrób zdjęcie'}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        style={{ display: 'none' }}
      />
    </div>
  );
};

// === WSPÓLNY FORMULARZ START / END ===
const DayForm = ({ mode, driver, activeLog, onCancel, onDone }) => {
  // mode: 'start' lub 'end'
  const isStart = mode === 'start';
  const [cars, setCars] = useState([]);
  const [carId, setCarId] = useState(activeLog?.carId || '');
  const [mileage, setMileage] = useState('');
  const [fuel, setFuel] = useState(50);
  const [cleanExt, setCleanExt] = useState(true);
  const [cleanInt, setCleanInt] = useState(true);
  const [hasDamage, setHasDamage] = useState(false);
  const [damageNotes, setDamageNotes] = useState('');
  const [notes, setNotes] = useState('');
  const [cleanReturned, setCleanReturned] = useState(true);
  const [hasNewDamage, setHasNewDamage] = useState(false);
  const [newDamageNotes, setNewDamageNotes] = useState('');
  const [keyLocation, setKeyLocation] = useState('');
  const [wearingUniform, setWearingUniform] = useState(true);
  const [dayFeedback, setDayFeedback] = useState('');
  const [photos, setPhotos] = useState({});
  const [uploadStates, setUploadStates] = useState({}); // { slot: {uploading, failed} }
  const [submitting, setSubmitting] = useState(false);

  const handleUploadState = useCallback((slot, state) => {
    setUploadStates((prev) => ({ ...prev, [slot]: state }));
  }, []);

  useEffect(() => {
    if (!isStart) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/jerry-drive/cars?location=${LOCATION}`);
        setCars(r.data.items || []);
      } catch (e) {
        toast.error('Błąd ładowania samochodów');
      }
    })();
  }, [isStart]);

  const photoOK = PHOTO_FIELDS.every((f) => photos[f.key]);
  const anyUploading = Object.values(uploadStates).some((s) => s?.uploading);
  const anyFailed = Object.values(uploadStates).some((s) => s?.failed);
  const baseFormOK = mileage !== '' && (!isStart || carId);
  const canSubmit = baseFormOK && photoOK && !anyUploading && !anyFailed && (!isStart || (cars.length > 0));

  const submit = async () => {
    if (!canSubmit) {
      if (anyUploading) {
        toast.error('⏳ Poczekaj — jeszcze wysyłam zdjęcia');
      } else if (anyFailed) {
        toast.error('❌ Niektóre zdjęcia nie zostały wysłane — kliknij „↻ Spróbuj ponownie" pod czerwonymi kafelkami');
      } else if (!photoOK) {
        toast.error('Zrób wszystkie 6 zdjęć');
      } else {
        toast.error('Uzupełnij wszystkie pola formularza');
      }
      return;
    }
    setSubmitting(true);
    try {
      if (isStart) {
        await axios.post(`${API}/api/jerry-drive/logs/start`, {
          location: LOCATION,
          driverId: driver.id,
          carId,
          mileage: parseInt(mileage, 10),
          fuel: parseInt(fuel, 10),
          cleanExterior: cleanExt,
          cleanInterior: cleanInt,
          hasDamage,
          damageNotes,
          notes,
          wearingUniform,
          photos,
        });
        toast.success('✅ Dzień rozpoczęty!');
      } else {
        if (!keyLocation.trim()) {
          toast.error('Podaj gdzie zostawiłeś kluczyki');
          setSubmitting(false);
          return;
        }
        await axios.post(`${API}/api/jerry-drive/logs/${activeLog.id}/end`, {
          mileage: parseInt(mileage, 10),
          fuel: parseInt(fuel, 10),
          cleanReturned,
          hasNewDamage,
          newDamageNotes,
          keyLocation,
          notes,
          dayFeedback,
          photos,
        });
        toast.success('✅ Dzień zakończony!');
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h2 style={{ margin: 0, color: colors.text, fontSize: 22 }}>
        {isStart ? '🚗 Rozpoczęcie dnia' : '🏁 Zakończenie dnia'} • {driver.name}
      </h2>

      {isStart && (
        <div>
          <label style={labelStyle}>Wybierz samochód *</label>
          {cars.length === 0 ? (
            <div style={{ color: colors.red, fontSize: 13 }}>Brak samochodów. Manager musi dodać.</div>
          ) : (
            <select value={carId} onChange={(e) => setCarId(e.target.value)} style={inputStyle} data-testid="jd-car-select">
              <option value="">— wybierz —</option>
              {cars.map((c) => <option key={c.id} value={c.id}>{c.name} {c.plate ? `(${c.plate})` : ''}</option>)}
            </select>
          )}
        </div>
      )}

      {!isStart && activeLog && (
        <div style={{ padding: 10, background: colors.panel, borderRadius: 8, fontSize: 13, color: colors.muted }}>
          🚗 {activeLog.carName} • Start: {activeLog.start?.mileage?.toLocaleString('pl-PL')} km
        </div>
      )}

      <div>
        <label style={labelStyle}>{isStart ? 'Stan licznika (start) *' : 'Stan licznika (koniec) *'} </label>
        <input
          type="number" inputMode="numeric"
          value={mileage} onChange={(e) => setMileage(e.target.value)}
          placeholder="np. 123456" style={inputStyle}
          data-testid="jd-mileage-input"
        />
      </div>

      <div>
        <label style={labelStyle}>Poziom paliwa: <b style={{ color: colors.gold }}>{fuel}%</b></label>
        <input type="range" min="0" max="100" value={fuel} onChange={(e) => setFuel(e.target.value)}
          style={{ width: '100%' }} data-testid="jd-fuel-input" />
      </div>

      {isStart ? (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ flex: 1, minWidth: 140, display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: colors.panel, borderRadius: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={cleanExt} onChange={(e) => setCleanExt(e.target.checked)} />
              <span style={{ color: colors.text, fontSize: 13 }}>Auto czyste z zewnątrz</span>
            </label>
            <label style={{ flex: 1, minWidth: 140, display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: colors.panel, borderRadius: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={cleanInt} onChange={(e) => setCleanInt(e.target.checked)} />
              <span style={{ color: colors.text, fontSize: 13 }}>Auto czyste w środku</span>
            </label>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: colors.panel, borderRadius: 10, cursor: 'pointer', border: hasDamage ? `2px solid ${colors.red}` : `1px solid ${colors.border}` }}>
            <input type="checkbox" checked={hasDamage} onChange={(e) => setHasDamage(e.target.checked)} />
            <span style={{ color: hasDamage ? colors.red : colors.text, fontSize: 13, fontWeight: 600 }}>⚠️ Widoczne uszkodzenia</span>
          </label>
          {hasDamage && (
            <textarea value={damageNotes} onChange={(e) => setDamageNotes(e.target.value)}
              placeholder="Opisz uszkodzenia…" rows={2}
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
          )}

          {/* Reprezentacja firmy — strój */}
          <label
            data-testid="jd-uniform-checkbox"
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: 12,
              background: colors.panel, borderRadius: 10, cursor: 'pointer',
              border: !wearingUniform ? `2px solid ${colors.gold}` : `1px solid ${colors.border}`,
            }}>
            <input type="checkbox" checked={wearingUniform} onChange={(e) => setWearingUniform(e.target.checked)} />
            <span style={{ color: wearingUniform ? colors.text : colors.gold, fontSize: 13, fontWeight: 600 }}>
              👔 Jestem w odzieży firmowej i dobrze reprezentuję firmę
            </span>
          </label>
        </>
      ) : (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: colors.panel, borderRadius: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={cleanReturned} onChange={(e) => setCleanReturned(e.target.checked)} />
            <span style={{ color: colors.text, fontSize: 13 }}>Auto oddane czyste</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: colors.panel, borderRadius: 10, cursor: 'pointer', border: hasNewDamage ? `2px solid ${colors.red}` : `1px solid ${colors.border}` }}>
            <input type="checkbox" checked={hasNewDamage} onChange={(e) => setHasNewDamage(e.target.checked)} />
            <span style={{ color: hasNewDamage ? colors.red : colors.text, fontSize: 13, fontWeight: 600 }}>⚠️ Nowe uszkodzenia podczas dnia</span>
          </label>
          {hasNewDamage && (
            <textarea value={newDamageNotes} onChange={(e) => setNewDamageNotes(e.target.value)}
              placeholder="Opisz nowe uszkodzenia…" rows={2}
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
          )}
          <div>
            <label style={labelStyle}>Gdzie zostawiłeś kluczyki? *</label>
            <input value={keyLocation} onChange={(e) => setKeyLocation(e.target.value)}
              placeholder="np. szuflada w biurze" style={inputStyle} data-testid="jd-keys-input" />
          </div>

          <div>
            <label style={labelStyle}>💬 Uwagi do dnia — dostawy, skargi, problemy, propozycje</label>
            <textarea
              value={dayFeedback}
              onChange={(e) => setDayFeedback(e.target.value)}
              placeholder="Np. dostawa od X była spóźniona 30 min; klient na ul. Y zgłosił reklamację; brakuje miejsca na parking; itp."
              rows={4}
              data-testid="jd-day-feedback"
              style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>
        </>
      )}

      <div>
        <label style={labelStyle}>{isStart ? 'Uwagi' : 'Uwagi końcowe'}</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Cokolwiek wartego odnotowania…" rows={2}
          style={{ ...inputStyle, fontFamily: 'inherit', resize: 'vertical' }} />
      </div>

      {/* === ZDJĘCIA === */}
      <div style={{ marginTop: 6 }}>
        <div style={{ marginBottom: 10, padding: 12, background: 'rgba(245, 158, 11, 0.1)', borderRadius: 10, border: `1px solid ${colors.gold}` }}>
          <div style={{ fontSize: 14, color: colors.gold, fontWeight: 700, marginBottom: 4 }}>
            📸 Wymagane 6 zdjęć — {PHOTO_FIELDS.filter((f) => photos[f.key]).length}/6
            {anyUploading && <span style={{ marginLeft: 8, color: '#fbbf24', fontSize: 12 }}>⏳ wysyłam…</span>}
            {anyFailed && <span style={{ marginLeft: 8, color: '#ef4444', fontSize: 12 }}>❌ powtórz uploady</span>}
          </div>
          <div style={{ fontSize: 12, color: colors.muted }}>
            Bez kompletu nie zapiszesz. Zdjęcia są kompresowane automatycznie.
            {anyFailed && ' Zaznaczone na czerwono zdjęcia trzeba ponownie wysłać przyciskiem „↻ Spróbuj ponownie".'}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
          {PHOTO_FIELDS.map((f) => (
            <PhotoPicker
              key={f.key}
              label={f.label}
              value={photos[f.key]}
              onChange={(v) => setPhotos((p) => ({ ...p, [f.key]: v }))}
              testId={`jd-photo-${f.key}`}
              slot={f.key}
              onUploadState={handleUploadState}
            />
          ))}
        </div>
      </div>

      {/* === AKCJE === */}
      <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
        <button
          onClick={submit}
          disabled={!canSubmit || submitting}
          data-testid="jd-submit-btn"
          style={{
            flex: 2, padding: '14px', borderRadius: 12,
            background: canSubmit ? (isStart ? colors.green : colors.gold) : colors.muted,
            color: 'white', border: 'none', fontSize: 16, fontWeight: 700,
            cursor: submitting ? 'wait' : (canSubmit ? 'pointer' : 'not-allowed'),
          }}>
          {submitting ? '⏳ Zapisuję…' : (isStart ? '✅ Rozpocznij dzień' : '🏁 Zakończ dzień')}
        </button>
        <button
          onClick={onCancel}
          disabled={submitting}
          style={{
            flex: 1, padding: '14px', borderRadius: 12,
            background: 'transparent', color: colors.text,
            border: `1px solid ${colors.border}`, cursor: 'pointer', fontSize: 15,
          }}>
          Anuluj
        </button>
      </div>
    </div>
  );
};

// === REPORT FAULT MODAL (zgłoszenie usterki auta — może być bez aktywnego dnia) ===
const FAULT_CATEGORIES = [
  { key: 'engine', label: '🔧 Silnik / rozruch' },
  { key: 'body', label: '🚙 Karoseria / lakier' },
  { key: 'tires', label: '🛞 Opony / koła' },
  { key: 'brakes', label: '🛑 Hamulce' },
  { key: 'electric', label: '⚡ Elektronika / światła' },
  { key: 'interior', label: '🪑 Wnętrze / klimatyzacja' },
  { key: 'fluids', label: '💧 Płyny / oleje' },
  { key: 'other', label: '❓ Inne' },
];
const FAULT_SEVERITIES = [
  { key: 'low', label: 'Niski', color: '#10b981', hint: 'Da się jeździć, do naprawy przy okazji' },
  { key: 'medium', label: 'Średni', color: '#f59e0b', hint: 'Naprawa w tym tygodniu' },
  { key: 'high', label: 'Wysoki', color: '#ef4444', hint: 'Auto niezdatne / niebezpieczne' },
];

const ReportFaultModal = ({ driver, activeLog, onClose, onDone }) => {
  const [cars, setCars] = useState([]);
  const [carId, setCarId] = useState(activeLog?.carId || '');
  const [category, setCategory] = useState('other');
  const [severity, setSeverity] = useState('medium');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/jerry-drive/cars?location=${LOCATION}`);
        const list = r.data.items || [];
        setCars(list);
        // Default: aktualne auto z activeLog lub pierwsze z listy
        if (!carId && activeLog?.carId) setCarId(activeLog.carId);
        else if (!carId && list.length) setCarId(list[0].id);
      } catch (e) {
        toast.error('Błąd ładowania listy samochodów');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLog?.carId]);

  const submit = async () => {
    if (!carId) { toast.error('Wybierz samochód'); return; }
    if (!description.trim()) { toast.error('Opisz krótko usterkę'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/jerry-drive/faults`, {
        location: LOCATION,
        carId,
        driverId: driver?.id || null,
        logId: activeLog?.id || null,
        category,
        severity,
        description: description.trim(),
      });
      toast.success('Zgłoszenie wysłane — manager już to widzi');
      onDone?.();
    } catch (e) {
      toast.error('Błąd wysyłki: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="jd-fault-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(2, 6, 23, 0.85)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto',
        background: `linear-gradient(180deg, ${colors.panel}, #0b1220)`,
        border: `1px solid ${colors.border}`,
        borderRadius: 18, padding: 22, color: colors.text,
        boxShadow: '0 30px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <span style={{ fontSize: 28 }}>⚠️</span>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, color: '#fff' }}>Zgłoś usterkę</h3>
            <div style={{ fontSize: 12, color: colors.muted, marginTop: 2 }}>
              Trafi bezpośrednio do managera
            </div>
          </div>
        </div>

        <label style={labelStyle}>Samochód</label>
        {loading ? (
          <div style={{ color: colors.muted, fontSize: 13, padding: '10px 0' }}>Ładowanie…</div>
        ) : cars.length === 0 ? (
          <div style={{ color: colors.muted, fontSize: 13 }}>Brak samochodów.</div>
        ) : (
          <select
            value={carId}
            onChange={(e) => setCarId(e.target.value)}
            data-testid="jd-fault-car"
            style={inputStyle}
          >
            {cars.map((c) => (
              <option key={c.id} value={c.id}>
                🚗 {c.name}{c.plate ? ` • ${c.plate}` : ''}
              </option>
            ))}
          </select>
        )}

        <label style={{ ...labelStyle, marginTop: 14 }}>Kategoria</label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
          {FAULT_CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setCategory(c.key)}
              data-testid={`jd-fault-cat-${c.key}`}
              style={{
                padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                background: category === c.key ? 'rgba(6, 182, 212, 0.18)' : '#0b1220',
                border: `1px solid ${category === c.key ? colors.accent : colors.border}`,
                color: '#fff', textAlign: 'left', fontSize: 13, fontWeight: 600,
              }}
            >
              {c.label}
            </button>
          ))}
        </div>

        <label style={{ ...labelStyle, marginTop: 14 }}>Poziom pilności</label>
        <div style={{ display: 'flex', gap: 6 }}>
          {FAULT_SEVERITIES.map((s) => (
            <button
              key={s.key}
              onClick={() => setSeverity(s.key)}
              title={s.hint}
              data-testid={`jd-fault-sev-${s.key}`}
              style={{
                flex: 1, padding: '12px 8px', borderRadius: 10, cursor: 'pointer',
                background: severity === s.key ? `${s.color}22` : '#0b1220',
                border: `1px solid ${severity === s.key ? s.color : colors.border}`,
                color: severity === s.key ? s.color : '#fff', fontSize: 13, fontWeight: 700,
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: colors.muted, marginTop: 6, lineHeight: 1.4 }}>
          {FAULT_SEVERITIES.find((s) => s.key === severity)?.hint}
        </div>

        <label style={{ ...labelStyle, marginTop: 14 }}>Opis usterki</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="np. Dziwny hałas z silnika przy przyspieszaniu, wibracje w kierownicy powyżej 80 km/h..."
          rows={4}
          maxLength={1000}
          data-testid="jd-fault-desc"
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
        <div style={{ fontSize: 11, color: colors.muted, marginTop: 4, textAlign: 'right' }}>
          {description.length} / 1000
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button
            onClick={onClose}
            disabled={saving}
            data-testid="jd-fault-cancel"
            style={{
              flex: 1, padding: '12px', borderRadius: 10, cursor: saving ? 'not-allowed' : 'pointer',
              background: 'transparent', border: `1px solid ${colors.border}`,
              color: colors.muted, fontSize: 14, fontWeight: 600,
            }}
          >
            Anuluj
          </button>
          <button
            onClick={submit}
            disabled={saving || !carId || !description.trim()}
            data-testid="jd-fault-submit"
            style={{
              flex: 2, padding: '12px', borderRadius: 10,
              cursor: saving || !carId || !description.trim() ? 'not-allowed' : 'pointer',
              background: !carId || !description.trim()
                ? 'rgba(148,163,184,0.18)'
                : 'linear-gradient(135deg, #dc2626, #ef4444)',
              color: '#fff', border: 'none', fontSize: 14, fontWeight: 800,
              boxShadow: !carId || !description.trim() ? 'none' : '0 8px 20px rgba(239,68,68,0.35)',
            }}
          >
            {saving ? 'Wysyłam…' : '⚠️ Zgłoś usterkę'}
          </button>
        </div>
      </div>
    </div>
  );
};


// === SWAP CAR MODAL (zmiana samochodu w trakcie aktywnej zmiany) ===
const SwapCarModal = ({ activeLog, onClose, onDone }) => {
  const [cars, setCars] = useState([]);
  const [newCarId, setNewCarId] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/jerry-drive/cars?location=${LOCATION}`);
        setCars((r.data.items || []).filter((c) => c.id !== activeLog?.carId));
      } catch (e) {
        toast.error('Błąd ładowania listy samochodów');
      } finally {
        setLoading(false);
      }
    })();
  }, [activeLog?.carId]);

  const submit = async () => {
    if (!newCarId) { toast.error('Wybierz nowy samochód'); return; }
    if (!reason.trim()) { toast.error('Podaj powód zmiany'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/jerry-drive/logs/${activeLog.id}/swap-car`, {
        newCarId,
        reason: reason.trim(),
      });
      toast.success('Samochód zmieniony — kontynuuj zmianę nowym autem');
      onDone?.();
    } catch (e) {
      toast.error('Błąd zapisu: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="jd-swap-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(2, 6, 23, 0.85)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 480,
        background: `linear-gradient(180deg, ${colors.panel}, #0b1220)`,
        border: `1px solid ${colors.border}`,
        borderRadius: 18, padding: 22, color: colors.text,
        boxShadow: '0 30px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
          <span style={{ fontSize: 28 }}>🔄</span>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, color: '#fff' }}>Zmień samochód</h3>
            <div style={{ fontSize: 12, color: colors.muted, marginTop: 2 }}>
              Aktualne auto: <b style={{ color: '#fff' }}>{activeLog?.carName}</b>{activeLog?.carPlate ? ` • ${activeLog.carPlate}` : ''}
            </div>
          </div>
        </div>
        <div style={{
          margin: '14px 0 18px', padding: '10px 12px', borderRadius: 10,
          background: 'rgba(245, 158, 11, 0.12)',
          border: '1px solid rgba(245, 158, 11, 0.35)',
          fontSize: 12, color: '#fcd34d', lineHeight: 1.45,
        }}>
          ℹ️ Twoja zmiana będzie kontynuowana nowym samochodem. Cały dzień zostanie zapisany jako jeden wpis z historią zmian aut. Manager zobaczy to w panelu Jerry Drive.
        </div>

        <label style={labelStyle}>Wybierz nowy samochód</label>
        {loading ? (
          <div style={{ color: colors.muted, fontSize: 13, padding: '10px 0' }}>Ładowanie…</div>
        ) : cars.length === 0 ? (
          <div style={{ color: colors.muted, fontSize: 13, padding: '10px 0' }}>
            Brak innych samochodów w bazie. Skontaktuj się z managerem.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
            {cars.map((c) => (
              <button
                key={c.id}
                onClick={() => setNewCarId(c.id)}
                data-testid={`jd-swap-car-${c.id}`}
                style={{
                  padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                  background: newCarId === c.id ? 'rgba(6, 182, 212, 0.18)' : '#0b1220',
                  border: `1px solid ${newCarId === c.id ? colors.accent : colors.border}`,
                  color: '#fff', textAlign: 'left', fontSize: 14, fontWeight: 600,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  transition: 'all 0.15s',
                }}
              >
                <span>🚗 {c.name}</span>
                {c.plate && <span style={{ fontSize: 12, color: colors.muted, fontFamily: 'monospace' }}>{c.plate}</span>}
              </button>
            ))}
          </div>
        )}

        <label style={labelStyle}>Powód zmiany</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="np. awaria silnika, pęknięta opona, brak paliwa..."
          rows={3}
          maxLength={500}
          data-testid="jd-swap-reason"
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
        <div style={{ fontSize: 11, color: colors.muted, marginTop: 4, textAlign: 'right' }}>
          {reason.length} / 500
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button
            onClick={onClose}
            disabled={saving}
            data-testid="jd-swap-cancel"
            style={{
              flex: 1, padding: '12px', borderRadius: 10, cursor: saving ? 'not-allowed' : 'pointer',
              background: 'transparent', border: `1px solid ${colors.border}`,
              color: colors.muted, fontSize: 14, fontWeight: 600,
            }}
          >
            Anuluj
          </button>
          <button
            onClick={submit}
            disabled={saving || !newCarId || !reason.trim()}
            data-testid="jd-swap-confirm"
            style={{
              flex: 2, padding: '12px', borderRadius: 10,
              cursor: saving || !newCarId || !reason.trim() ? 'not-allowed' : 'pointer',
              background: !newCarId || !reason.trim()
                ? 'rgba(148,163,184,0.18)'
                : `linear-gradient(135deg, ${colors.accentSoft}, ${colors.accent})`,
              color: '#fff', border: 'none', fontSize: 14, fontWeight: 800,
              boxShadow: !newCarId || !reason.trim() ? 'none' : '0 8px 20px rgba(6,182,212,0.35)',
            }}
          >
            {saving ? 'Zapisuję…' : '🔄 Zmień samochód'}
          </button>
        </div>
      </div>
    </div>
  );
};

// === GŁÓWNY KOMPONENT ===
// Props:
//   onClose (optional) — jeśli podane, komponent renderuje przycisk "Zamknij" zamiast linku powrotu (tryb modalny)
export default function JerryDrivePublic({ onClose }) {
  const isModal = typeof onClose === 'function';
  const [drivers, setDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [mode, setMode] = useState(null); // null | 'start' | 'end'
  const [activeLog, setActiveLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [showFaultModal, setShowFaultModal] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/jerry-drive/drivers?location=${LOCATION}`);
        setDrivers(r.data.items || []);
      } catch (e) {
        toast.error('Błąd ładowania kierowców');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Po wyborze kierowcy — pobierz aktywny log (jeśli jest)
  const refreshActiveLog = async (driverId = selectedDriverId) => {
    if (!driverId) { setActiveLog(null); return; }
    try {
      const r = await axios.get(`${API}/api/jerry-drive/logs/active`, {
        params: { location: LOCATION, driver_id: driverId },
      });
      setActiveLog(r.data?.log || null);
    } catch (e) { /* brak — ok */ }
  };

  useEffect(() => {
    if (!selectedDriverId) { setActiveLog(null); return; }
    refreshActiveLog(selectedDriverId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriverId]);

  const driver = drivers.find((d) => d.id === selectedDriverId);

  // Helper: inicjały kierowcy do awatara
  const getInitials = (name) => {
    if (!name) return '?';
    return name.trim().split(/\s+/).slice(0, 2).map((n) => n[0]).join('').toUpperCase();
  };

  // Stała paleta kolorów dla awatarów (deterministic per name)
  const avatarColors = ['#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', '#06b6d4', '#ef4444', '#84cc16'];
  const colorFor = (name) => {
    const idx = Math.abs([...(name || '?')].reduce((a, c) => a + c.charCodeAt(0), 0)) % avatarColors.length;
    return avatarColors[idx];
  };

  const containerStyle = {
    // W trybie modal (wywołanie z ekranu logowania) — pełny fixed overlay,
    // żeby JerryDrive pokazywał się jako osobne okno nad wszystkim (nie "obok" ekranu logowania).
    ...(isModal
      ? { position: 'fixed', inset: 0, zIndex: 99998, overflowY: 'auto', overflowX: 'hidden' }
      : { minHeight: '100vh', position: 'relative', overflow: 'hidden' }),
    background: `
      radial-gradient(ellipse at 50% -20%, rgba(6,182,212,0.18), transparent 60%),
      radial-gradient(ellipse at 100% 100%, rgba(99,102,241,0.12), transparent 50%),
      ${colors.bg}
    `,
    color: colors.text,
    padding: 'max(16px, env(safe-area-inset-top)) 16px 30px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };
  const cardStyle = {
    maxWidth: 760, margin: '0 auto',
    background: 'linear-gradient(180deg, rgba(17,24,39,0.85), rgba(10,15,30,0.92))',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    borderRadius: 20,
    padding: 24,
    border: `1px solid rgba(6,182,212,0.18)`,
    boxShadow: '0 20px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
    position: 'relative',
    zIndex: 2,
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', color: colors.muted, padding: 30 }}>⏳ Ładowanie…</div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Globalna animacja CSS dla wizualizacji */}
      <style>{`
        @keyframes jd-road-flow { 0% { transform: translateY(-100%); } 100% { transform: translateY(0); } }
        @keyframes jd-pulse-ring { 0% { transform: scale(1); opacity: 0.6; } 100% { transform: scale(2.2); opacity: 0; } }
        @keyframes jd-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        @keyframes jd-grid-shift { 0% { background-position: 0 0; } 100% { background-position: 40px 40px; } }
      `}</style>

      {/* Animowana siatka w tle */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0, zIndex: 0,
        background: `
          linear-gradient(rgba(6,182,212,0.05) 1px, transparent 1px) 0 0/40px 40px,
          linear-gradient(90deg, rgba(6,182,212,0.05) 1px, transparent 1px) 0 0/40px 40px
        `,
        animation: 'jd-grid-shift 12s linear infinite',
        maskImage: 'radial-gradient(ellipse at center, black 0%, transparent 70%)',
        WebkitMaskImage: 'radial-gradient(ellipse at center, black 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: 760, margin: '0 auto 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Animowane logo z pulsem */}
          <div style={{ position: 'relative', width: 48, height: 48 }}>
            <div style={{
              position: 'absolute', inset: 0, borderRadius: '50%',
              background: `radial-gradient(circle, ${colors.accent}, transparent 70%)`,
              animation: 'jd-pulse-ring 2.5s ease-out infinite',
            }} />
            <div style={{
              position: 'relative', width: 48, height: 48, borderRadius: '50%',
              background: `linear-gradient(135deg, ${colors.accent}, ${colors.indigo})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 24, boxShadow: `0 6px 20px ${colors.accent}55`,
              animation: 'jd-float 3.5s ease-in-out infinite',
            }}>🚗</div>
          </div>
          <div>
            <h1 style={{
              margin: 0, fontSize: 26, fontWeight: 900,
              background: `linear-gradient(135deg, #fff 30%, ${colors.accent})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
              letterSpacing: '-0.5px',
            }}>Jerry Drive</h1>
            <div style={{ fontSize: 11, color: colors.muted, letterSpacing: 2, textTransform: 'uppercase', marginTop: 2 }}>
              System kontroli aut firmowych
            </div>
          </div>
        </div>
        {isModal ? (
          <button
            type="button"
            onClick={onClose}
            data-testid="jd-close-btn"
            style={{
              background: 'rgba(239, 68, 68, 0.12)',
              border: `1px solid ${colors.red}66`,
              color: colors.red,
              padding: '8px 14px',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 700,
              cursor: 'pointer',
              backdropFilter: 'blur(8px)',
            }}
          >
            ✕ Zamknij
          </button>
        ) : (
          <a href="/" style={{ color: colors.muted, fontSize: 13, textDecoration: 'none' }}>← Powrót do logowania</a>
        )}
      </div>

      <div style={cardStyle}>
        {!mode ? (
          <>
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: colors.accent, fontWeight: 800, letterSpacing: 2, marginBottom: 12, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: colors.accent, boxShadow: `0 0 8px ${colors.accent}` }} />
                Krok 1 — kim jesteś?
              </div>
              {drivers.length === 0 ? (
                <div style={{ padding: 16, background: 'rgba(99, 102, 241, 0.1)', borderRadius: 12, color: colors.muted, fontSize: 13, border: '1px solid rgba(99,102,241,0.3)' }}>
                  Brak kierowców na liście. Manager musi Cię dodać w Panel Managera → Jerry Drive → Kierowcy.
                </div>
              ) : (
                <div
                  data-testid="jd-driver-grid"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: 12,
                  }}>
                  {drivers.map((d) => {
                    const selected = d.id === selectedDriverId;
                    const c = colorFor(d.name);
                    return (
                      <button
                        key={d.id}
                        type="button"
                        onClick={() => setSelectedDriverId(d.id)}
                        data-testid={`jd-driver-card-${d.id}`}
                        style={{
                          padding: '16px 8px',
                          borderRadius: 14,
                          background: selected ? `linear-gradient(135deg, ${c}33, ${c}11)` : 'rgba(255,255,255,0.03)',
                          border: `2px solid ${selected ? c : 'rgba(255,255,255,0.08)'}`,
                          cursor: 'pointer',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 10,
                          transition: 'all 0.2s ease',
                          transform: selected ? 'translateY(-2px)' : 'none',
                          boxShadow: selected ? `0 8px 20px ${c}44` : 'none',
                        }}>
                        <div style={{
                          width: 56, height: 56, borderRadius: '50%',
                          background: `linear-gradient(135deg, ${c}, ${c}cc)`,
                          color: '#fff', fontWeight: 800, fontSize: 22,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: `0 4px 12px ${c}66`,
                        }}>
                          {getInitials(d.name)}
                        </div>
                        <div style={{
                          fontSize: 13, fontWeight: 700,
                          color: selected ? '#fff' : colors.text,
                          textAlign: 'center', lineHeight: 1.2,
                        }}>{d.name}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {driver && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ fontSize: 11, color: colors.accent, fontWeight: 800, letterSpacing: 2, marginTop: 8, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: colors.accent, boxShadow: `0 0 8px ${colors.accent}` }} />
                  Krok 2 — co robisz?
                </div>
                {activeLog && (
                  <div style={{
                    padding: 14, borderRadius: 12,
                    background: 'linear-gradient(135deg, rgba(6,182,212,0.18), rgba(99,102,241,0.08))',
                    border: `1px solid ${colors.accent}66`,
                    fontSize: 13, color: '#a5f3fc',
                    display: 'flex', alignItems: 'center', gap: 10,
                  }}>
                    <span style={{ fontSize: 22 }}>⏱️</span>
                    <div>
                      Masz otwarty dzień autem <b style={{ color: '#fff' }}>{activeLog.carName}</b> od <b style={{ color: '#fff' }}>{new Date(activeLog.startedAt).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}</b>.
                      Musisz go zakończyć.
                    </div>
                  </div>
                )}

                {/* Akcje — duże kafelki z wizualizacjami */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
                  {/* START — zielony z animowaną drogą */}
                  <button
                    onClick={() => setMode('start')}
                    disabled={!!activeLog}
                    data-testid="jd-btn-start"
                    style={{
                      position: 'relative', overflow: 'hidden',
                      padding: '20px', borderRadius: 18, minHeight: 130,
                      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                      background: activeLog
                        ? 'rgba(148,163,184,0.1)'
                        : 'linear-gradient(135deg, #064e3b 0%, #047857 50%, #10b981 100%)',
                      color: activeLog ? colors.muted : '#fff',
                      border: activeLog ? '1px solid rgba(148,163,184,0.2)' : '1px solid rgba(16,185,129,0.4)',
                      cursor: activeLog ? 'not-allowed' : 'pointer',
                      textAlign: 'left',
                      boxShadow: activeLog ? 'none' : '0 16px 40px rgba(16,185,129,0.35), inset 0 1px 0 rgba(255,255,255,0.15)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                    onMouseEnter={(e) => { if (!activeLog) { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 20px 50px rgba(16,185,129,0.55), inset 0 1px 0 rgba(255,255,255,0.2)'; } }}
                    onMouseLeave={(e) => { if (!activeLog) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(16,185,129,0.35), inset 0 1px 0 rgba(255,255,255,0.15)'; } }}
                  >
                    {/* Animowana droga */}
                    {!activeLog && (
                      <div aria-hidden style={{
                        position: 'absolute', right: 10, top: 0, bottom: 0, width: 16,
                        backgroundImage: 'repeating-linear-gradient(0deg, rgba(255,255,255,0.6) 0 12px, transparent 12px 26px)',
                        animation: 'jd-road-flow 1.2s linear infinite',
                        opacity: 0.35,
                        maskImage: 'linear-gradient(180deg, transparent, black 30%, black 70%, transparent)',
                        WebkitMaskImage: 'linear-gradient(180deg, transparent, black 30%, black 70%, transparent)',
                        pointerEvents: 'none',
                      }} />
                    )}
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.7, marginBottom: 6 }}>
                      Start
                    </div>
                    <div style={{ fontSize: 32, marginBottom: 4, lineHeight: 1 }}>🚗</div>
                    <div style={{ fontSize: 17, fontWeight: 800, marginTop: 6, lineHeight: 1.15 }}>Rozpocznij dzień</div>
                    <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4, lineHeight: 1.4 }}>
                      Sprawdź auto, zrób 6 zdjęć i ruszaj
                    </div>
                  </button>

                  {/* END — cyan / indigo gradient zamiast pomarańczowego */}
                  <button
                    onClick={() => setMode('end')}
                    disabled={!activeLog}
                    data-testid="jd-btn-end"
                    style={{
                      position: 'relative', overflow: 'hidden',
                      padding: '20px', borderRadius: 18, minHeight: 130,
                      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                      background: !activeLog
                        ? 'rgba(148,163,184,0.1)'
                        : 'linear-gradient(135deg, #0e7490 0%, #06b6d4 50%, #6366f1 100%)',
                      color: !activeLog ? colors.muted : '#fff',
                      border: !activeLog ? '1px solid rgba(148,163,184,0.2)' : '1px solid rgba(6,182,212,0.5)',
                      cursor: !activeLog ? 'not-allowed' : 'pointer',
                      textAlign: 'left',
                      boxShadow: !activeLog ? 'none' : '0 16px 40px rgba(6,182,212,0.35), inset 0 1px 0 rgba(255,255,255,0.15)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                    onMouseEnter={(e) => { if (activeLog) { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 20px 50px rgba(6,182,212,0.55), inset 0 1px 0 rgba(255,255,255,0.2)'; } }}
                    onMouseLeave={(e) => { if (activeLog) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(6,182,212,0.35), inset 0 1px 0 rgba(255,255,255,0.15)'; } }}
                  >
                    {/* Wizualizacja: prędkościomierz / dial */}
                    {activeLog && (
                      <svg aria-hidden viewBox="0 0 100 100" style={{
                        position: 'absolute', right: -4, top: -4, width: 72, height: 72, opacity: 0.22, pointerEvents: 'none',
                      }}>
                        <circle cx="50" cy="50" r="40" fill="none" stroke="#fff" strokeWidth="2" strokeDasharray="4 4" />
                        <circle cx="50" cy="50" r="30" fill="none" stroke="#fff" strokeWidth="1.5" />
                        <line x1="50" y1="50" x2="78" y2="32" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
                        <circle cx="50" cy="50" r="3" fill="#fff" />
                      </svg>
                    )}
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.7, marginBottom: 6 }}>
                      Meta
                    </div>
                    <div style={{ fontSize: 32, marginBottom: 4, lineHeight: 1 }}>🏁</div>
                    <div style={{ fontSize: 17, fontWeight: 800, marginTop: 6, lineHeight: 1.15 }}>Zakończ dzień</div>
                    <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4, lineHeight: 1.4 }}>
                      Stan końcowy, kluczyki i feedback
                    </div>
                  </button>

                  {/* ZMIEŃ SAMOCHÓD — widoczny tylko gdy zmiana aktywna */}
                  {activeLog && (
                    <button
                      onClick={() => setShowSwapModal(true)}
                      data-testid="jd-btn-swap-car"
                      style={{
                        position: 'relative', overflow: 'hidden',
                        padding: '20px', borderRadius: 18, minHeight: 130,
                        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                        background: 'linear-gradient(135deg, #78350f 0%, #d97706 50%, #f59e0b 100%)',
                        color: '#fff',
                        border: '1px solid rgba(245, 158, 11, 0.5)',
                        cursor: 'pointer', textAlign: 'left',
                        boxShadow: '0 16px 40px rgba(245, 158, 11, 0.30), inset 0 1px 0 rgba(255,255,255,0.15)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 20px 50px rgba(245, 158, 11, 0.5), inset 0 1px 0 rgba(255,255,255,0.2)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(245, 158, 11, 0.30), inset 0 1px 0 rgba(255,255,255,0.15)'; }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.7, marginBottom: 6 }}>
                        Awaria
                      </div>
                      <div style={{ fontSize: 32, marginBottom: 4, lineHeight: 1 }}>🔄</div>
                      <div style={{ fontSize: 17, fontWeight: 800, marginTop: 6, lineHeight: 1.15 }}>Zmień samochód</div>
                      <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4, lineHeight: 1.4 }}>
                        Auto zepsute? Wybierz inne i kontynuuj zmianę
                      </div>
                    </button>
                  )}

                  {/* ZGŁOŚ USTERKĘ — widoczny zawsze (kierowca może zgłosić też przed startem) */}
                  <button
                    onClick={() => setShowFaultModal(true)}
                    data-testid="jd-btn-report-fault"
                    style={{
                      position: 'relative', overflow: 'hidden',
                      padding: '20px', borderRadius: 18, minHeight: 130,
                      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                      background: 'linear-gradient(135deg, #7f1d1d 0%, #dc2626 50%, #ef4444 100%)',
                      color: '#fff',
                      border: '1px solid rgba(239, 68, 68, 0.5)',
                      cursor: 'pointer', textAlign: 'left',
                      boxShadow: '0 16px 40px rgba(239, 68, 68, 0.30), inset 0 1px 0 rgba(255,255,255,0.15)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 20px 50px rgba(239, 68, 68, 0.5), inset 0 1px 0 rgba(255,255,255,0.2)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(239, 68, 68, 0.30), inset 0 1px 0 rgba(255,255,255,0.15)'; }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase', opacity: 0.7, marginBottom: 6 }}>
                      Usterka
                    </div>
                    <div style={{ fontSize: 32, marginBottom: 4, lineHeight: 1 }}>⚠️</div>
                    <div style={{ fontSize: 17, fontWeight: 800, marginTop: 6, lineHeight: 1.15 }}>Zgłoś usterkę</div>
                    <div style={{ fontSize: 11, opacity: 0.85, marginTop: 4, lineHeight: 1.4 }}>
                      Coś nie działa? Powiedz managerowi teraz
                    </div>
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <DayForm
            mode={mode}
            driver={driver}
            activeLog={activeLog}
            onCancel={() => setMode(null)}
            onDone={() => { setMode(null); setActiveLog(null); setSelectedDriverId(''); }}
          />
        )}
      </div>

      {showSwapModal && activeLog && (
        <SwapCarModal
          activeLog={activeLog}
          onClose={() => setShowSwapModal(false)}
          onDone={async () => {
            setShowSwapModal(false);
            await refreshActiveLog();
          }}
        />
      )}

      {showFaultModal && (
        <ReportFaultModal
          driver={driver}
          activeLog={activeLog}
          onClose={() => setShowFaultModal(false)}
          onDone={() => setShowFaultModal(false)}
        />
      )}
    </div>
  );
}
